/**
 * Mode « Simulation » (demande utilisateur) : le modèle déroule UN tournoi
 * complet, animé comme le legacy — d'abord la phase de groupes (classements en
 * FLIP), le tableau final apparaît aux 16es, puis champion + podium.
 *
 * Sélecteur de modèle à quatre axes :
 *  - Scénario : « le plus probable » (déterministe) ou « aléatoire » (tirage seedé) ;
 *  - Notes : Elo réel (eloratings.net) ou Elo legacy (v2) ;
 *  - Départ : tournoi complet ou « depuis le réel » (résultats joués figés) ;
 *  - TAB : gardiens (EA FC 26) ou 50/50.
 *
 * Réutilise l'animateur du replay (`runOpeningReplay`) et les composants du
 * dashboard (groupes, 3es, bracket, Elo) sur l'état dérivé du scénario.
 */
import { simulateScenario } from "../app/scenario.ts";
import type { RatingsModel, ScenarioResult, ScenarioType, StartFrom } from "../app/scenario.ts";
import type { OfficialState } from "../app/officialStore.ts";
import { groups } from "../app/data.ts";
import type { ShootoutModel } from "../engine/shootout.ts";
import type { FixedResult } from "../engine/montecarlo.ts";
import { renderGroupsSkeleton } from "./components/groupsGrid.ts";
import { renderBracket, fitBracket } from "./components/bracketView.ts";
import { renderEloRanking } from "./components/eloRanking.ts";
import { runOpeningReplay, type ReplayHandle } from "./replay.ts";
import { koFlagFlight } from "./koTransition.ts";
import { currentValueElos, refreshValueElos } from "../app/valueModel.ts";
import { flagSpan, esc } from "./html.ts";
import { t } from "../app/i18n.ts";

interface RealSnapshot {
  readonly fixed: Record<number, FixedResult>;
  readonly finishedCount: number;
}

interface SimOpts {
  scenarioType: ScenarioType;
  ratings: RatingsModel;
  startFrom: StartFrom;
  shootoutModel: ShootoutModel;
}

interface Segment {
  readonly key: keyof SimOpts;
  readonly label: string;
  readonly options: ReadonlyArray<{ readonly v: string; readonly l: string }>;
}

const SEGMENTS: readonly Segment[] = [
  {
    key: "scenarioType",
    label: "sim.seg.scenario",
    options: [
      { v: "modal", l: "sim.scenario.modal" },
      { v: "random", l: "sim.scenario.random" },
    ],
  },
  {
    key: "ratings",
    label: "lb.col.ratings",
    options: [
      { v: "real", l: "sim.ratings.real" },
      { v: "legacy", l: "sim.ratings.legacy" },
      { v: "value", l: "sim.ratings.value" },
    ],
  },
  {
    key: "startFrom",
    label: "sim.seg.startFrom",
    options: [
      { v: "fromReal", l: "sim.startFrom.fromReal" },
      { v: "scratch", l: "sim.startFrom.scratch" },
    ],
  },
  {
    key: "shootoutModel",
    label: "common.penaltiesAbbr",
    options: [
      { v: "gk", l: "sim.shootout.gk" },
      { v: "fiftyFifty", l: "50/50" },
    ],
  },
];

const TAB_FR: Record<ShootoutModel, string> = { gk: "gardiens", fiftyFifty: "50/50" };
// i18n keys (resolved at render time, not module load) for the model-label values.
const RATINGS_FR_KEYS: Record<RatingsModel, string> = {
  real: "sim.ratings.real",
  legacy: "sim.ratings.legacy",
  value: "sim.ratings.value",
};

export class Simulation {
  // Défauts : scénario basé sur l'Elo (« le plus probable », déterministe) et
  // on REPART des résultats déjà terminés — l'utilisateur peut basculer sur
  // « Aléatoire » et/ou « Tournoi complet ».
  private opts: SimOpts = { scenarioType: "modal", ratings: "real", startFrom: "fromReal", shootoutModel: "gk" };

  private seedCounter = 1;
  private started = false;
  private runToken = 0;
  /** Nombre de matchs réels figés lors du dernier run (pour resynchroniser). */
  private lastRealFinished: number | null = null;
  /** État du dernier scénario rendu (pour le tooltip de parcours au survol). */
  private lastOfficial: OfficialState | null = null;
  private replay: ReplayHandle | null = null;
  private readonly getReal: () => RealSnapshot;

  private readonly els: {
    models: HTMLElement;
    shuffle: HTMLButtonElement;
    label: HTMLElement;
    scoreboard: HTMLElement;
    champion: HTMLElement;
    groups: HTMLElement;
    thirds: HTMLElement;
    bracket: HTMLElement;
    elo: HTMLElement;
    foot: HTMLElement;
  };

  constructor(container: HTMLElement, deps: { getReal: () => RealSnapshot }) {
    this.getReal = deps.getReal;
    // Valeurs d'effectif fraîches (serveur) pour le modèle « Elo + valeur » ;
    // sans serveur, la graine committée est déjà chargée (meilleur effort).
    void refreshValueElos();
    container.innerHTML = `
      <div class="card sim-controls">
        <div class="sim-head">
          <h2>${t("sim.title")}</h2>
          <p class="note">${t("sim.intro")}</p>
        </div>
        <div class="sim-models" id="simModels"></div>
        <div class="sim-actions">
          <button id="simShuffle" class="primary"><i class="ti ti-dice-5" aria-hidden="true"></i> ${t("sim.newDraw")}</button>
          <span id="simModelLabel" class="note"></span>
        </div>
      </div>
      <section id="sim-scoreboard" class="hide"></section>
      <section id="sim-champion" class="hide"></section>
      <section id="sim-groups"></section>
      <section id="sim-thirds" class="hide"></section>
      <section id="sim-bracket" class="hide"></section>
      <section id="sim-elo" class="hide"></section>
      <footer id="sim-foot" class="note foot"></footer>
    `;
    const pick = (id: string): HTMLElement => {
      const el = container.querySelector<HTMLElement>(id);
      if (!el) throw new Error(`simulation: section manquante ${id}`);
      return el;
    };
    this.els = {
      models: pick("#simModels"),
      shuffle: pick("#simShuffle") as HTMLButtonElement,
      label: pick("#simModelLabel"),
      scoreboard: pick("#sim-scoreboard"),
      champion: pick("#sim-champion"),
      groups: pick("#sim-groups"),
      thirds: pick("#sim-thirds"),
      bracket: pick("#sim-bracket"),
      elo: pick("#sim-elo"),
      foot: pick("#sim-foot"),
    };
    this.renderControls();
    this.els.shuffle.onclick = (): void => {
      // Aléatoire : nouveau tirage. Modal (déterministe) : on rejoue le même.
      if (this.opts.scenarioType === "random") this.seedCounter += 1;
      this.run();
    };
    renderGroupsSkeleton(this.els.groups, groups);
    // resize throttlé (rAF) et seulement si le bracket est visible (évite le
    // layout inutile quand on n'est pas sur l'onglet Simulation).
    let rafId = 0;
    window.addEventListener("resize", () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        if (!this.els.bracket.classList.contains("hide")) fitBracket(this.els.bracket);
      });
    });
  }

  /**
   * Activation de l'onglet. Première fois : lance une simulation. Ensuite, si
   * on est « depuis le réel » et que de NOUVEAUX matchs sont terminés, on
   * resynchronise (les scores réels figés doivent rester à jour). Sinon on
   * laisse le dernier scénario affiché.
   */
  activate(): void {
    if (!this.started) {
      this.started = true;
      this.run();
      return;
    }
    if (this.opts.startFrom === "fromReal" && this.getReal().finishedCount !== this.lastRealFinished) {
      this.run();
    }
  }

  private renderControls(): void {
    this.els.models.innerHTML = SEGMENTS.map(
      (s) => `
      <div class="seg" data-key="${s.key}">
        <span class="seg-label">${esc(t(s.label))}</span>
        <div class="seg-btns" role="group" aria-label="${esc(t(s.label))}">
          ${s.options
            .map(
              (o) =>
                `<button type="button" data-val="${o.v}" class="${this.opts[s.key] === o.v ? "on" : ""}">${esc(o.v === "fiftyFifty" ? o.l : t(o.l))}</button>`,
            )
            .join("")}
        </div>
      </div>`,
    ).join("");
    this.els.models.onclick = (ev): void => {
      const btn = (ev.target as HTMLElement).closest<HTMLButtonElement>(".seg-btns button");
      if (!btn) return;
      const seg = btn.closest<HTMLElement>(".seg");
      const key = seg?.dataset.key as keyof SimOpts | undefined;
      const val = btn.dataset.val;
      if (!key || !val || this.opts[key] === val) return;
      (this.opts as unknown as Record<string, string>)[key] = val;
      seg?.querySelectorAll<HTMLButtonElement>(".seg-btns button").forEach((b) =>
        b.classList.toggle("on", b === btn),
      );
      this.run();
    };
  }

  private run(): void {
    const token = ++this.runToken;
    this.replay?.cancel(); // l'ancien onComplete est ignoré (garde de token)

    const real = this.opts.startFrom === "fromReal" ? this.getReal() : null;
    this.lastRealFinished = real ? real.finishedCount : null;
    const result = simulateScenario({
      scenarioType: this.opts.scenarioType,
      ratings: this.opts.ratings,
      startFrom: this.opts.startFrom,
      shootoutModel: this.opts.shootoutModel,
      seed: `wc26-sim-${this.seedCounter}`,
      ...(real ? { fixedResults: real.fixed } : {}),
      ...(this.opts.ratings === "value" ? { valueElos: currentValueElos() } : {}),
    });
    this.lastOfficial = result.official;

    this.updateShuffleButton();
    this.renderModelLabel(real);

    // Mise en scène « d'abord les groupes » : on masque le reste, on réaffiche
    // les groupes (la transition KO a pu les cacher au run précédent).
    this.els.champion.classList.add("hide");
    this.els.champion.innerHTML = "";
    this.els.thirds.classList.add("hide");
    this.els.bracket.classList.add("hide");
    this.els.bracket.innerHTML = "";
    this.els.elo.classList.add("hide");
    this.els.groups.classList.remove("hide");
    renderGroupsSkeleton(this.els.groups, groups);

    this.replay = runOpeningReplay({
      official: result.official,
      scoreboardEl: this.els.scoreboard,
      groupsEl: this.els.groups,
      force: true,
      elos: result.elos,
      flipSeedPrefix: result.drawSeed,
      // Cadence posée pour SUIVRE le déroulé (≈320 ms/match à x1) ; les boutons
      // x2–x8 accélèrent par-dessus.
      pace: { minPerMatchMs: 320, maxPerMatchMs: 1200, targetTotalMs: 32_000 },
      // Le passage au tableau attend un CLIC (« Passer au tableau ») : le temps
      // de lire les classements finals des poules avant le vol des drapeaux.
      koClickToContinue: true,
      onReachKO: () => {
        if (token !== this.runToken) return;
        // « Vol des drapeaux » : les groupes s'effacent, les qualifiés glissent
        // vers le tableau (legacy). Annulable si un nouveau run démarre en plein vol.
        void koFlagFlight(this.els.groups, this.els.bracket, result.official, () => token !== this.runToken);
      },
      onComplete: () => {
        if (token !== this.runToken) return;
        this.renderFinal(result);
      },
    });
  }

  private renderFinal(result: ScenarioResult): void {
    // Les groupes ont « disparu » au passage en KO : on reste sur le tableau.
    this.els.groups.classList.add("hide");
    this.els.thirds.classList.add("hide");
    this.els.bracket.classList.remove("hide");
    renderBracket(this.els.bracket, result.official, null);
    fitBracket(this.els.bracket);
    this.renderChampion(result);
    renderEloRanking(this.els.elo, result.official);
    this.els.elo.classList.remove("hide");
    this.renderFooter(result);
  }

  private updateShuffleButton(): void {
    this.els.shuffle.innerHTML =
      this.opts.scenarioType === "random"
        ? `<i class="ti ti-dice-5" aria-hidden="true"></i> ${t("sim.newDraw")}`
        : `<i class="ti ti-rotate-clockwise" aria-hidden="true"></i> ${t("sim.replay")}`;
  }

  private renderChampion(result: ScenarioResult): void {
    this.els.champion.classList.remove("hide");
    this.els.champion.innerHTML = `
      <div class="card podium">
        <div class="pod-title"><i class="ti ti-trophy" aria-hidden="true"></i> ${t("sim.championTitle")}</div>
        <div class="pod-champ">${flagSpan(result.champion, { name: true })}</div>
        <div class="pod-rest">
          <div class="pod-row"><span class="medal">🥈</span> <span class="pod-rk">${t("sim.runnerUp")}</span> ${flagSpan(result.runnerUp, { name: true })}</div>
          <div class="pod-row"><span class="medal">🥉</span> <span class="pod-rk">${t("sim.thirdPlace")}</span> ${flagSpan(result.third, { name: true })}</div>
        </div>
        <button id="simReplay" class="linklike"><i class="ti ti-player-play" aria-hidden="true"></i> ${t("sim.replayScenario")}</button>
      </div>`;
    const replayBtn = this.els.champion.querySelector<HTMLButtonElement>("#simReplay");
    if (replayBtn) replayBtn.onclick = (): void => this.run();
  }

  private renderModelLabel(real: RealSnapshot | null): void {
    const scenarioVal =
      this.opts.scenarioType === "random" ? t("sim.scenarioFr.random") : t("sim.scenarioFr.modal");
    const ratingsVal = RATINGS_FR_KEYS[this.opts.ratings];
    const startVal = this.opts.startFrom === "scratch" ? t("sim.startFr.scratch") : t("sim.startFr.fromReal");
    const fixedSuffix = real ? ` ${t("sim.label.fixedCount", { n: real.finishedCount })}` : "";
    const tabVal = this.opts.shootoutModel === "gk" ? t("sim.tabFr.gk") : TAB_FR.fiftyFifty;
    const parts = [
      t("sim.label.scenario", { value: `<strong>${scenarioVal}</strong>` }),
      t("sim.label.ratings", { value: `<strong>${t(ratingsVal)}</strong>` }),
      t("sim.label.startFrom", { value: `<strong>${startVal}${fixedSuffix}</strong>` }),
      t("sim.label.shootout", { value: `<strong>${tabVal}</strong>` }),
    ];
    this.els.label.innerHTML = parts.join(" · ");
  }

  private renderFooter(result: ScenarioResult): void {
    const tail =
      this.opts.scenarioType === "random" ? t("sim.footer.relaunch") : t("sim.footer.deterministic");
    this.els.foot.innerHTML = `
      ${t("sim.footer.oneOfMany", { tail })}.
      ${t("sim.footer.model", { ratings: t(RATINGS_FR_KEYS[this.opts.ratings]) })}
      <br>${t("sim.footer.champion", { code: `<strong>${esc(result.champion ?? "?")}</strong>` })}`;
  }

  /** État du dernier scénario rendu (pour le tooltip de parcours au survol). */
  getOfficial(): OfficialState | null {
    return this.lastOfficial;
  }

  dispose(): void {
    this.replay?.cancel();
  }
}
